<script setup lang="ts">
import type { Company } from "@asap/shared/schemas/company";
import type { FactoringStatus, PaymentMethod } from "@asap/shared/types/supabase.types.gen";
import type { AddressTableInsert } from "@asap/shared/utils";
import { translateFactoringStatus, translatePaymentMethod } from "@asap/shared/utils/translate";
import { useCompanyApi } from "~/components/company/composables/useCompanyApi";
import { useUserMe } from "~/components/user/composables/useUser";
import { useGoogleMapsUtils } from "~/composables/useGoogleMaps";
import { useToast } from "~/composables/useToast";

const props = defineProps<{
  company: Company;
}>();

const emit = defineEmits<{
  onUpdate: [company: Company];
}>();

const { t } = useI18n();
const { updateCompany: updateCompanyRequest } = useCompanyApi();
const { isAccounting } = useUserMe();
const { searchUrl } = useGoogleMapsUtils();
const { addToastError, addToastSuccess } = useToast();

const location = ref<string | undefined>(undefined);
const billingProcess = ref<string | undefined>(undefined);
const billingAddress = ref<AddressTableInsert>();
const paymentMethod = ref<PaymentMethod | undefined>(undefined);
const factoringStatus = ref<FactoringStatus | undefined>(undefined);
const isUpdating = ref(false);

const selectablePaymentMethods: PaymentMethod[] = ["bank_transfer", "check", "bill_of_exchange"];
const paymentMethodItems = selectablePaymentMethods.map((value) => ({
  value,
  label: translatePaymentMethod.fr[value],
}));
const factoringStatuses: FactoringStatus[] = ["active", "inactive"];
const factoringStatusItems = factoringStatuses.map((value) => ({
  value,
  label: translateFactoringStatus.fr[value],
}));

onMounted(() => {
  billingProcess.value = props.company.billing_process ?? undefined;
  billingAddress.value = props.company.billing_address ?? undefined;
  paymentMethod.value = props.company.payment_method ?? undefined;
  factoringStatus.value = props.company.factoring_status ?? undefined;
});

const updateCompany = async (close: () => void) => {
  try {
    isUpdating.value = true;
    const updatedCompany = await updateCompanyRequest(props.company.id, {
      billing_process: billingProcess.value,
      billing_address_id: billingAddress.value?.id,
      payment_method: paymentMethod.value,
      factoring_status: factoringStatus.value,
    });
    close();
    addToastSuccess({ description: t("companies.updateModal.toast.updated", { name: props.company.name }) });
    emit("onUpdate", updatedCompany);
  } catch (error) {
    addToastError(
      { title: t("companies.updateModal.toast.updateError"), description: t("companies.toast.contactSupport") },
      error
    );
  } finally {
    isUpdating.value = false;
  }
};

const ui = {
  title: "flex flex-row items-center gap-2 text-secondary-900 font-size-4",
  titleIcon: "font-size-4",
  titleText: "font-medium",
  body: "w-[97%] pl-[2.5%] mt-5",
  fields: "flex flex-col gap-4",
  field: "w-full",
  footer: "flex justify-end w-full gap-2",
};
</script>

<template>
  <AwModal position="right" size="md">
    <template #default>
      <AwButton color="secondary" class="ml-auto" variant="outline">
        <AwIcon name="i-ph-pen" />
        {{ $t("companies.updateModal.title") }}
      </AwButton>
    </template>
    <template #title>
      <div :class="ui.title">
        <AwIcon name="i-ph-building-office-duotone" :class="ui.titleIcon" />
        <div :class="ui.titleText">{{ $t("companies.updateModal.title") }}</div>
      </div>
    </template>
    <template #body>
      <div :class="ui.body">
        <div :class="ui.fields">
          <AwFormField :label="$t('companies.createModal.billingAddress.label')">
            <AddressTableResultItem
              v-if="billingAddress"
              :address="billingAddress"
              is-removable
              @on-remove="
                billingAddress = undefined;
                location = undefined;
              "
            />
            <AddressTableInput
              v-else
              v-model:location="location"
              v-model:address="billingAddress"
              :placeholder="$t('companies.createModal.billingAddress.placeholder')"
            />
            <NuxtLink v-if="billingAddress" target="_blank" :href="searchUrl(billingAddress.formatted)">
              <Button variant="ghost-link">
                {{ $t("companies.createModal.billingAddress.openInGoogleMaps") }}
                <ButtonIcon :icon="mapIcon.external_link" />
              </Button>
            </NuxtLink>
          </AwFormField>
          <AwFormField :label="$t('companies.createModal.billingProcess.label')" :class="ui.field">
            <AwTextarea
              :class="ui.field"
              :placeholder="$t('companies.createModal.billingProcess.placeholder')"
              :model-value="billingProcess"
              @update:model-value="billingProcess = $event"
            />
          </AwFormField>
          <AwFormField :label="$t('companies.createModal.paymentMethod.label')" :class="ui.field">
            <AwSelectMenu
              v-model="paymentMethod"
              :class="ui.field"
              :search-input="false"
              :placeholder="$t('companies.createModal.paymentMethod.placeholder')"
              :items="paymentMethodItems"
              value-key="value"
              label-key="label"
            />
          </AwFormField>
          <AwFormField :label="$t('companies.updateModal.factoringStatus')" :class="ui.field">
            <AwRadioGroup
              v-model="factoringStatus"
              :disabled="!isAccounting"
              orientation="horizontal"
              fill
              variant="card"
              :items="factoringStatusItems"
            />
          </AwFormField>
        </div>
      </div>
    </template>
    <template #footer="{ close }">
      <div :class="ui.footer">
        <AwButton color="secondary" variant="ghost" @click="close()">{{ $t("common.actions.cancel") }}</AwButton>
        <AwButton color="secondary" :is-loading="isUpdating" @click="() => updateCompany(close)">
          {{ $t("companies.updateModal.title") }}
        </AwButton>
      </div>
    </template>
  </AwModal>
</template>
